var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-data-view.ts
import { Change } from "@adornis/base/change.js";
import "@adornis/baseql/entities/adornisEntity.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import "@adornis/chemistry/elements/components/x-flat-loader.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import "@adornis/chemistry/elements/components/x-loader.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { changeStream } from "@adornis/filter/change-stream.js";
import "@adornis/filter/client/x-filter-editor.js";
import { html, noChange } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from "rxjs";
import { DataViewController } from "./data-view-controller.js";
import {
  buildFullOptions,
  optionsFullyCalculated
} from "./data-view-options.js";
import "./x-data-view-card-list.js";
import "./x-data-view-table.js";
var XDataView = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.options = new RXController(
      this,
      null
    );
    this.fullOptions = new RXController(
      this,
      this.options.observable.pipe(
        filter(Boolean),
        map(
          (options) => options[optionsFullyCalculated] ? options : buildFullOptions(options, {}).dataViewOptions
        )
      )
    );
    this._dvController = new DataViewController(
      this,
      combineLatest({
        filter: this.fullOptions.observable.pipe(
          filter(Boolean),
          switchMap((options) => options.view.whenChanging.pipe(map((view) => view.filter?.toObject()))),
          filter(Boolean),
          distinctUntilChanged((a, b) => !Change.getDiff(a, b)?.length),
          map((filter2) => constructValue(filter2))
        ),
        operation: this.fullOptions.observable.pipe(
          filter(Boolean),
          map((options) => options.operation),
          distinctUntilChanged()
        ),
        finalSelectionSet: this.fullOptions.observable.pipe(
          filter(Boolean),
          switchMap(
            (options) => options.view.whenChanging.pipe(
              map((view) => view.toObject()),
              distinctUntilChanged(
                (a, b) => !Change.getDiff(a, b)?.find((diff) => diff.getPath().includes("configuredColumns"))
              ),
              map(() => options)
            )
          ),
          map((options) => options.finalSelectionSet()),
          distinctUntilChanged(
            (a, b) => !Change.getDiff(a, b)?.filter((ch) => !["_id", "_class"].includes(ch.getPath().slice(-1)[0]))?.length
          )
        )
      })
    );
    this.viewMode = new RXController(
      this,
      this.fullOptions.observable.pipe(
        map((options) => options?.view.mode),
        distinctUntilChanged()
      )
    );
  }
  connectedCallback() {
    super.connectedCallback();
    this.fullOptions.observable.pipe(
      filter(Boolean),
      switchMap(
        (options) => changeStream(options.view.whenChanging.pipe(map((view) => view.toObject()))).pipe(
          filter(Boolean),
          filter((ch) => !!ch.length),
          filter((ch) => ch.some((ch2) => !!ch2.getPath().length)),
          // filter out setting a completely new view, that's only done on initialization
          filter((ch) => ch.some((ch2) => !ch2.concernsPath(["selectedItems"]))),
          tap(
            (x) => console.log(
              "bubbling changes to persistence",
              x.map((c) => `${c.getPath().join(".")} - ${c.getValue()}`)
            )
          ),
          map(() => options)
        )
      ),
      debounceTime(100)
      // avoid deadlocks. not a real fix but does quite well. Fix if it acts up
    ).subscribe((options) => options.onViewChanged(options.view));
  }
  render() {
    switch (this.viewMode.value) {
      case "list":
      case "kanban":
      case "table":
      default:
        return this.tableView();
    }
  }
  shouldUpdate(_changedProperties) {
    if (_changedProperties.size === 1 && _changedProperties.has("options"))
      return false;
    return super.shouldUpdate(_changedProperties);
  }
  tableView() {
    if (!this._dvController)
      return noChange;
    return xComponents(html`
      <x-data-view-table
        .data=${this._dvController.data}
        .fullOptions=${this.fullOptions.value}
        .loading=${this._dvController.loadingInitially || !this._dvController.complete}
        @sort=${(e) => {
      const filter2 = this.fullOptions.value?.view.filter;
      if (!filter2 || !e.detail.columnFieldName)
        return;
      const sortingFieldIndex = filter2.sortingFields.indexOf(e.detail.columnFieldName);
      if (sortingFieldIndex > -1) {
        if (e.detail.sortingDirection) {
          filter2.sortingDirections[sortingFieldIndex] = e.detail.sortingDirection;
        } else {
          filter2.sortingFields.splice(sortingFieldIndex, 1);
          filter2.sortingDirections.splice(sortingFieldIndex, 1);
        }
      } else {
        filter2.sortingFields.unshift(e.detail.columnFieldName);
        filter2.sortingDirections.unshift(e.detail.sortingDirection ?? "asc");
      }
      if (!filter2.sortingFields.length) {
        filter2.sortingFields.push("_id");
        filter2.sortingDirections.push("desc");
      }
    }}
        .renderedIndexCallback=${(num) => this.virtualizationOnRender(num)}
      ></x-data-view-table>
    `);
  }
  virtualizationOnRender(index) {
    if ((index === this._dvController.data.length - 20 || index === this._dvController.data.length - 2) && this._dvController.totalRequested < index + 80) {
      this._dvController.next();
    }
  }
  cardListView() {
    return xComponents(html`
      <x-data-view-card-list
        .data=${this._dvController?.data}
        .fullOptions=${this.fullOptions.value}
      ></x-data-view-card-list>
    `);
  }
};
__decorateClass([
  property({ attribute: false })
], XDataView.prototype, "options", 2);
__decorateClass([
  state()
], XDataView.prototype, "fullOptions", 2);
__decorateClass([
  state()
], XDataView.prototype, "_dvController", 2);
__decorateClass([
  state()
], XDataView.prototype, "viewMode", 2);
XDataView = __decorateClass([
  customElement("x-data-view")
], XDataView);
export {
  XDataView
};
